import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useMatch } from "react-router";
import { useTranslation } from "react-i18next";
import { useOpenMajorBugCount } from "@/api/bugs";
import { useSprints } from "@/api/sprints";
import { useMe, useProjects } from "@/api/hooks";
import type { Project } from "@/api/types/Project";
import { Icon, Wordmark, type IconName } from "@/components/Icon";
import { ProjectLogo } from "@/components/ProjectLogo";
import { CommandPalette, SearchButton } from "@/components/CommandPalette";
import { NotificationBell } from "@/components/NotificationBell";
import { TimerPill, TimerProvider } from "@/components/work/Timer";
import { Avatar, cx } from "@/components/ui";
import { setLocale } from "@/i18n";
import { useThemeMode } from "@/lib/theme";

/** Signed-in layout: sidebar (stacks on top on narrow screens) + page content. */
export function AppShell() {
  const me = useMe();
  useThemeMode(me.theme_mode);
  useEffect(() => setLocale(me.locale), [me.locale]);

  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setNavOpen(false), [location.pathname]);

  return (
    <TimerProvider>
      <div className="flex min-h-screen flex-col md:flex-row">
        <Sidebar open={navOpen} onToggle={() => setNavOpen((o) => !o)} />
        <main className="flex min-w-0 flex-1 flex-col">
          <Outlet />
        </main>
        <CommandPalette />
      </div>
    </TimerProvider>
  );
}

function Sidebar({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const isPlatformManager = me.role === "manager";
  // The tester board, for people who test (or manage) somewhere.
  const { data: projects } = useProjects();
  const tests = !!projects?.some((p) => p.my_role === "tester" || p.my_role === "manager");

  return (
    <aside className="flex flex-col gap-3.5 border-b border-nav-border bg-nav-bg px-2.5 py-3.5 text-nav-fg md:sticky md:top-0 md:h-screen md:w-[236px] md:shrink-0 md:border-r md:border-b-0">
      <div className="flex items-center justify-between">
        <Link to="/projects" className="flex items-center gap-2 px-2 py-1 text-nav-fg no-underline hover:text-nav-fg">
          <Wordmark className="text-[21px]" />
        </Link>
        <div className="flex items-center gap-0.5">
          <span className="md:hidden">
            <TimerPill compact />
          </span>
          <NotificationBell />
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls="sidebar-nav"
            aria-label={t("nav.openMenu")}
            className="rounded-md p-2 text-nav-muted hover:bg-nav-active md:hidden"
          >
            <Icon name="menu" />
          </button>
        </div>
      </div>

      <div className="hidden md:block empty:hidden">
        <TimerPill />
      </div>

      <div id="sidebar-nav" className={cx("min-h-0 flex-1 flex-col gap-3.5", open ? "flex" : "hidden md:flex")}>
        {/* The links scroll on their own; the profile below stays pinned to the bottom. */}
        <div className="-mx-2.5 flex min-h-0 flex-1 flex-col gap-3.5 px-2.5 md:overflow-y-auto">
          <SearchButton />

          <nav aria-label={t("nav.main")} className="flex flex-col gap-0.5">
            <NavItem to="/inbox" icon="inbox" label={t("nav.inbox")} />
            <NavItem to="/tasks" icon="checklist" label={t("work.myWork")} />
            {tests && <NavItem to="/testing" icon="test" label={t("testingBoard.nav")} />}
            <NavItem to="/projects" icon="folder" label={t("nav.projects")} end />
            <NavItem to="/analytics" icon="chart" label={t("analytics.nav")} />
          </nav>

          <ProjectTree />

          {isPlatformManager && (
            <>
              <div className="px-2.5 pt-1.5 label-caps text-nav-muted">{t("nav.admin")}</div>
              <nav aria-label={t("nav.admin")} className="flex flex-col gap-0.5">
                <NavItem to="/admin/users" icon="shield" label={t("nav.users")} />
                <NavItem to="/admin/tags" icon="tag" label={t("work.tagsAdmin")} />
                <NavItem to="/admin/gitlab" icon="branch" label={t("gitlabAdmin.title")} />
              </nav>
            </>
          )}

          <a
            href="/docs/"
            target="_blank"
            rel="noreferrer"
            className="mt-auto flex items-center gap-2.5 rounded-[var(--radius-ctl)] px-2.5 py-[7px] font-medium text-nav-muted no-underline hover:bg-nav-active hover:text-nav-fg"
          >
            <Icon name="ext" className="text-nav-muted" />
            <span className="truncate">{t("nav.apiDocs")}</span>
          </a>
        </div>

        <Link
          to="/profile"
          className="flex shrink-0 items-center gap-2.5 rounded-[var(--radius-ctl)] border-t border-nav-border px-2 py-2.5 text-nav-fg no-underline hover:bg-nav-active hover:text-nav-fg"
        >
          <Avatar name={me.display_name} bot={me.is_bot} size={28} />
          <span className="min-w-0 flex-1">
            <span className="block truncate leading-tight font-semibold">{me.display_name}</span>
            <small className="block text-xs leading-tight text-nav-muted">
              {t(`roles.${me.role}`)}
              {me.is_owner && ` · ${t("users.owner").toLowerCase()}`}
            </small>
          </span>
          <Icon name="user" className="text-nav-muted" />
        </Link>
      </div>
    </aside>
  );
}

function NavItem({
  to,
  icon,
  label,
  end,
  badge,
  alsoActive,
  nested,
  hint,
}: {
  to: string;
  icon: IconName;
  label: string;
  end?: boolean;
  badge?: { count: number; label: string };
  /** Highlighted on related pages too (e.g. Tasks on a task's own page). */
  alsoActive?: boolean;
  /** A page inside a project in the tree: a little tighter. */
  nested?: boolean;
  /** A short note after the label, e.g. "current". */
  hint?: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cx(
          "flex items-center gap-2.5 rounded-[var(--radius-ctl)] px-2.5 font-medium text-nav-fg no-underline hover:bg-nav-active hover:text-nav-fg",
          nested ? "py-[5px]" : "py-[7px]",
          (isActive || alsoActive) && "bg-nav-active shadow-[inset_0_0_0_1px_var(--nav-border)]",
        )
      }
    >
      <Icon name={icon} className="text-nav-muted" />
      <span className="truncate">{label}</span>
      {hint && !badge && <span className="ml-auto shrink-0 text-[11px] text-nav-muted">{hint}</span>}
      {badge && (
        <span
          title={badge.label}
          className="ml-auto rounded-full bg-[#b42318] px-1.5 text-[12px] leading-[18px] font-semibold text-white"
        >
          {badge.count}
          <span className="sr-only"> · {badge.label}</span>
        </span>
      )}
    </NavLink>
  );
}

const EXPANDED_KEY = "mixla.nav.expandedProjects";

function loadExpanded(): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Every project as a collapsible node with its pages under it. Only one project is open at a time, so the list
 * stays short: the project being viewed opens by itself and closes the others. The open one is remembered in this
 * browser.
 */
function ProjectTree() {
  const { t } = useTranslation();
  const { data: projects } = useProjects();
  const current = useMatch("/p/:key/*")?.params.key;
  // Older versions remembered several open projects; keep only the last one.
  const [expanded, setExpanded] = useState<string[]>(() => loadExpanded().slice(-1));

  const save = (next: string[]) => {
    setExpanded(next);
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify(next));
    } catch {
      // Private mode or blocked storage: it just won't be remembered.
    }
  };
  // Opening a project's page (from a link, search or the URL) unfolds it in the tree and folds the others.
  useEffect(() => {
    if (current) save([current]);
  }, [current]);

  const toggle = (key: string) => save(expanded.includes(key) ? [] : [key]);

  return (
    <nav aria-label={t("nav.projects")} className="flex flex-col gap-0.5">
      <div className="px-2.5 pt-1.5 pb-1 label-caps text-nav-muted">{t("nav.projects")}</div>
      {projects?.length === 0 && <span className="px-2.5 text-sm text-nav-muted">{t("nav.noProjects")}</span>}
      {projects?.map((p) => (
        <ProjectNode key={p.id} project={p} open={expanded.includes(p.key)} current={p.key === current} onToggle={() => toggle(p.key)} />
      ))}
    </nav>
  );
}

function ProjectNode({
  project,
  open,
  current,
  onToggle,
}: {
  project: Project;
  open: boolean;
  current: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const key = project.key;
  const pagesId = `nav-${key}`;
  // Only open projects ask for their S1/S2 count.
  const openMajor = useOpenMajorBugCount(open ? key : undefined);
  // A task's page highlights Tasks; the regression suite and learnings are tabs of the Bugs area.
  const task = useMatch("/p/:key/tasks/:taskKey/*");
  const learnings = useMatch("/p/:key/learnings");
  const onTask = task?.params.key === key;
  const regression = onTask && task?.params.taskKey?.toUpperCase() === "REGRESSION";
  // One sidebar link per area; the area's other pages are tabs on the page (see AreaTabs).
  const area = useMatch("/p/:key/:area/*");
  const inArea = (...areas: string[]) => area?.params.key === key && areas.includes(area.params.area ?? "");
  const manager = project.my_role === "manager";

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={pagesId}
        className={cx(
          "flex items-center gap-2 rounded-[var(--radius-ctl)] px-1.5 py-1.5 text-left hover:bg-nav-active",
          current && !open && "bg-nav-active",
        )}
      >
        <Icon name={open ? "down" : "right"} size={13} className="shrink-0 text-nav-muted" />
        <ProjectLogo project={project} size={22} />
        <span className="min-w-0 flex-1 truncate font-semibold">{project.name}</span>
      </button>
      {open && (
        <div id={pagesId} className="mt-0.5 mb-1 ml-[18px] flex flex-col gap-0.5 border-l border-nav-border pl-1.5">
          <NavItem nested to={`/p/${key}/overview`} icon="home" label={t("nav.overview")} />
          <NavItem nested to={`/p/${key}`} icon="list" label={t("nav.tasks")} end alsoActive={onTask && !regression} />
          {project.scrum_enabled && (
            <>
              <SprintNav projectKey={key} active={inArea("sprints")} />
              <NavItem nested to={`/p/${key}/backlog`} icon="backlog" label={t("sprints.backlogTitle")} />
            </>
          )}
          <NavItem
            nested
            to={`/p/${key}/bugs`}
            icon="bug"
            label={t("bugNav.bugs")}
            badge={openMajor > 0 ? { count: openMajor, label: t("bugNav.openMajor", { count: openMajor }) } : undefined}
            alsoActive={regression || learnings?.params.key === key}
          />
          {(project.my_role === "tester" || manager) && (
            <NavItem nested to={`/p/${key}/testing`} icon="test" label={t("testingBoard.nav")} />
          )}
          <NavItem nested to={`/p/${key}/epics`} icon="flag" label={t("nav.planning")} alsoActive={inArea("epics", "releases")} />
          <NavItem nested to={`/p/${key}/analytics`} icon="chart" label={t("nav.reports")} alsoActive={inArea("analytics", "flow", "standup")} />
          {manager ? (
            <NavItem nested to={`/p/${key}/settings`} icon="settings" label={t("nav.settings")} alsoActive={inArea("members")} />
          ) : (
            <NavItem nested to={`/p/${key}/members`} icon="users" label={t("nav.members")} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A project's sprint entries: the active sprint, the next planned one, the Backlog page and all sprints. Missing
 * entries are left out; a project without sprints gets one "Plan a sprint" link.
 */
function SprintNav({ projectKey, active: onSprints }: { projectKey: string; active: boolean }) {
  const { t } = useTranslation();
  const sprints = useSprints(projectKey);
  if (!sprints.data) return null;
  const active = sprints.data.find((s) => s.state === "active");
  // One link: the active sprint when there is one, otherwise all sprints. Every sprint page highlights it.
  if (active) {
    return (
      <NavItem nested to={`/p/${projectKey}/sprints/${active.number}`} icon="sprint" label={active.name} alsoActive={onSprints} hint={t("sprints.state.active")} />
    );
  }
  const label = sprints.data.length ? t("sprints.allSprints") : t("sprints.planASprint");
  return <NavItem nested to={`/p/${projectKey}/sprints`} icon="sprint" label={label} alsoActive={onSprints} />;
}
